import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../../shared/listView/ListTable';
import ListTableNameCell from '../../../shared/listView/ListTableNameCell';
import { productNamesService } from '../../../services/productNamesService';
import DeleteItemIcon from '../../../shared/DeleteItemIcon';
import RestoreItemIcon from '../../../shared/RestoreItemIcon';
import EditItemIcon from '../../../shared/EditItemIcon';
import { productDeleteService } from '../../../services/productDeleteService';
import { POPUP_KEY_DELETE_PRODUCT, POPUP_KEY_RESTORE_PRODUCT } from '../../../constant';
import useWindowDimensions from '../../../hooks/useWindowDimension';

type ProductRow = {
    id: string;
    productCode: string;
    isDeleted?: boolean;
};

type ProductTableProps = {
    items: ProductRow[];
    isLoading?: boolean;
};

const ProductTable = ({ items, isLoading = false }: ProductTableProps) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();

    const columns = useMemo<ListTableColumn<ProductRow>[]>(() => [
        {
            id: 'name',
            header: i18n.t('app.page.listView.name'),
            cell: (row) => (
                <ListTableNameCell itemCode={row} itemService={productNamesService} />
            ),
        },
        {
            id: 'code',
            header: i18n.t('app.page.listView.code'),
            className: 'list-table__td_code',
            cell: (row) => <span className="list-table__code">{row.productCode}</span>,
        },
        {
            id: 'status',
            header: i18n.t('app.page.listView.status'),
            className: 'list-table__td_status',
            cell: (row) => (
                row.isDeleted
                    ? <span className="list-table__chip">{i18n.t('app.page.listView.deleted')}</span>
                    : null
            ),
        },
        {
            id: 'actions',
            header: i18n.t('app.page.listView.actions'),
            className: 'list-table__td_actions',
            cell: (row) => (
                <ListTableActions>
                    {row.isDeleted ? (
                        <RestoreItemIcon itemId={row.id} popupKey={POPUP_KEY_RESTORE_PRODUCT} />
                    ) : (
                        <EditItemIcon pageUrl={`/product/item/${row.id}`} />
                    )}
                    <DeleteItemIcon
                        itemId={row.id}
                        itemDeleteService={productDeleteService}
                        popupKey={POPUP_KEY_DELETE_PRODUCT}
                    />
                </ListTableActions>
            ),
        },
    ], [i18n]);

    return (
        <ListTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            width={width}
            isLoading={isLoading}
            isRowDeleted={(row) => Boolean(row.isDeleted)}
            onRowClick={(row) => {
                if (!row.isDeleted) {
                    navigate(`/product/item/${row.id}`);
                }
            }}
        />
    );
};

export default ProductTable;
